import ajax from "../lib/ajax";
import moment from "moment";
import "moment-timezone";
import {
  getHomeRoute as resolveHomeRoute,
  getUserTypes,
  resolveActiveUserType,
  resolvePostLoginNavigation,
  ACTIVE_USER_TYPE_STORAGE_KEY,
} from "../auth/userTypeUtils";
import {
  MIGRATE_USERNAME_ROUTE,
  requiresUsernameMigration,
} from "../auth/migrationUtils";
const { default: DBStorage } = require("../lib/DBStorage");

class AuthService {
  async login(signData) {
    try {
      const response = await ajax.post("/login", signData);

      const { user, token } = response.data;
      if (!user || !token) {
        throw new Error("Unexpected response");
      }
      await Promise.all([DBStorage.set("user", user), this.setToken(token)]);

      const types = getUserTypes(user);
      if (types.length === 1) {
        await this.setActiveUserType(types[0]);
      } else {
        await this.clearActiveUserType();
      }

      return user;
    } catch (error) {
      throw error;
    }
  }

  async resetPasswordLink(usernameObj) {
    try {
      const response = await ajax.post(
        "/users/mail/password-reset",
        usernameObj
      );
      const data = response.data;
      return data;
    } catch (error) {
      throw error;
    }
  }

  async resetPassword(passwordObj) {
    try {
      const response = await ajax.post(
        "/users/mail/passwordUpdate",
        passwordObj
      );
      const data = response.data;
      return data;
    } catch (error) {
      throw error;
    }
  }

  async deploymentDate() {
    try {
      const response = await ajax.get("/users/deployment-date");
      const data = response.data;
      const date = data.data.deployment_date;
      const formatDate = moment(date).tz("Asia/Kolkata").format("DD-MM-YYYY");
      return formatDate;
    } catch (error) {
      throw error;
    }
  }

  async setToken(token) {
    await DBStorage.set("token", token);
  }

  async setUser(user) {
    await DBStorage.set("user", user);
  }

  async setActiveUserType(type) {
    await DBStorage.set(ACTIVE_USER_TYPE_STORAGE_KEY, type);
  }

  async getActiveUserType() {
    return (await DBStorage.get(ACTIVE_USER_TYPE_STORAGE_KEY)) || null;
  }

  async clearActiveUserType() {
    await DBStorage.remove(ACTIVE_USER_TYPE_STORAGE_KEY);
  }

  async getUser() {
    return (await DBStorage.get("user")) || null;
  }

  async getUserType() {
    const user = await this.getUser();
    const active = await this.getActiveUserType();
    return resolveActiveUserType(user, active);
  }

  async getHomeRoute() {
    const userType = await this.getUserType();
    if (!userType) return null;
    return resolveHomeRoute(userType);
  }

  async resolvePostLogin() {
    const user = await this.getUser();
    const active = await this.getActiveUserType();
    return resolvePostLoginNavigation(user, active);
  }

  async resolvePostLoginRoute(user) {
    const resolved = user || (await this.getUser());
    if (requiresUsernameMigration(resolved)) {
      return MIGRATE_USERNAME_ROUTE;
    }
    const next = await this.resolvePostLogin();
    return next.route;
  }

  async sendMigrateUsernameOtp({ email, current_username, consent }) {
    const response = await ajax.post("/migrate_username/send_otp", {
      email,
      current_username,
      consent,
    });
    return response.data;
  }

  async migrateUsername({ email, current_username, consent, otp }) {
    const response = await ajax.post("/migrate_username", {
      email,
      current_username,
      consent,
      otp,
    });
    const { user: updatedUser, token } = response.data;
    if (updatedUser) await this.setUser(updatedUser);
    if (token) await this.setToken(token);
    return response.data;
  }

  async completeOrgSelection(type) {
    await this.setActiveUserType(type);
    return resolveHomeRoute(type);
  }

  async getToken() {
    return (await DBStorage.get("token")) || null;
  }

  async logout() {
    await DBStorage.DELETE_ALL();
  }
}

const authService = new AuthService();
export default authService;
